import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLoading } from '../context/LoadingContext';
import {
  ArrowLeftIcon,
  PhotoIcon,
  ChatBubbleBottomCenterTextIcon,
  VideoCameraIcon,
  DocumentTextIcon,
  CodeBracketIcon,
  MusicalNoteIcon,
  SparklesIcon
} from '@heroicons/react/24/outline';

interface GenerationOption {
  id: string;
  title: string;
  description: string;
  icon: React.ForwardRefExoticComponent<React.SVGProps<SVGSVGElement>>;
  link: string;
  status: 'active' | 'coming-soon';
  badgeText?: string;
}

const generationOptions: GenerationOption[] = [
  {
    id: 'chat',
    title: 'AI Chat',
    description: 'Engage in natural conversations with our advanced AI assistant',
    icon: ChatBubbleBottomCenterTextIcon,
    link: '/chat',
    status: 'active',
    badgeText: 'Popular',
  },
  {
    id: 'image',
    title: 'Image Generation',
    description: 'Create unique and stunning images from text descriptions using advanced AI models',
    icon: PhotoIcon,
    link: '/image-generation',
    status: 'active',
    badgeText: 'Popular',
  },
  {
    id: 'video',
    title: 'Video Generation',
    description: 'Transform text descriptions into captivating videos using AI technology',
    icon: VideoCameraIcon,
    link: '/video-generation',
    status: 'coming-soon',
  //  badgeText: 'Beta',
  },
  {
    id: 'text',
    title: 'Text Generation',
    description: 'Create articles, stories, and creative writing with AI assistance',
    icon: DocumentTextIcon,
    link: '/text-generation',
    status: 'active',
    badgeText: 'New',
  },
  {
    id: 'code',
    title: 'Code Generation',
    description: 'Generate code snippets, debug issues, and get programming assistance',
    icon: CodeBracketIcon,
    link: '/code-generation',
    status: 'active',
    badgeText: 'Beta',
  },
  {
    id: 'hd-photo',
    title: 'HD Photo Converter',
    description: 'Create stunning AI-generated images in high resolution',
    icon: PhotoIcon,
    link: '/photo-hd',
    status: 'active',
    badgeText: 'New',
  },
  {
    id: 'anime',
    title: 'Photo to Anime',
    description: 'Transform your photos into beautiful anime-style artwork',
    icon: SparklesIcon,
    link: '/photo-anime',
    status: 'coming-soon',

  },
  {
    id: 'music',
    title: 'Music Generation',
    description: 'Compose unique musical pieces and melodies using AI technology',
    icon: MusicalNoteIcon,
    link: '/music-generation',
    status: 'coming-soon',
  },
];

const Generation: React.FC = () => {
  const activeOptions = generationOptions.filter(opt => opt.status === 'active');
  const comingSoonOptions = generationOptions.filter(opt => opt.status === 'coming-soon');
  const { setLoading } = useLoading();

  useEffect(() => {
    const initializePage = async () => {
      setLoading(true);
      try {
        // Simulate loading time for initial data fetch
        await new Promise(resolve => setTimeout(resolve, 1000));
      } finally {
        setLoading(false);
      }
    };

    initializePage();
  }, [setLoading]);

  return (
    <div className="min-h-screen bg-gray-900">
      {/* Header */}
      <div className="fixed top-0 left-0 right-0 z-50 bg-gray-900/80 backdrop-blur-lg border-b border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <Link
                to="/"
                className="inline-flex items-center text-gray-400 hover:text-white transition-colors"
              >
                <ArrowLeftIcon className="h-5 w-5" />
              </Link>
              <div className="flex items-center space-x-3">
                <div className="h-3 w-3 bg-purple-500 rounded-full animate-pulse"></div>
                <h1 className="text-xl font-bold text-white">Generation</h1>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24">
        {/* Header */}
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-white mb-4">NEXON AI Generation</h2>
          <p className="text-lg text-gray-300 max-w-2xl mx-auto">
            Explore our suite of AI-powered generation tools. Create images, code, text, and more with
            NEXON's state-of-the-art artificial intelligence.
          </p>
        </div>

        {/* Active Options */}
        <div className="mb-12">
          <h3 className="text-xl font-semibold text-white mb-6">Available Tools</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {activeOptions.map((option) => (
              <React.Fragment key={option.id}>
                {option.id === 'anime' ? (
                  <Link
                    to="/photo-anime"
                    className="block bg-gray-800 rounded-xl p-6 hover:bg-gray-700 transition-colors group"
                  >
                    <div className="w-12 h-12 bg-pink-500/10 rounded-lg flex items-center justify-center mb-4 group-hover:bg-pink-500/20 transition-colors">
                      <SparklesIcon className="w-6 h-6 text-pink-400" />
                    </div>
                    <h2 className="text-xl font-semibold text-white mb-2">Photo to Anime</h2>
                    <p className="text-gray-400">Transform your photos into beautiful anime-style artwork</p>
                  </Link>
                ) : (
                  <Link
                    to={option.link}
                    className="relative p-6 rounded-xl border border-gray-700 bg-gray-800/50 hover:bg-gray-800/80 transition-all duration-200 group hover:scale-[1.02]"
                  >
                    {option.badgeText && (
                      <span className={`absolute -top-3 right-4 px-3 py-1 text-xs font-semibold rounded-full text-white ${
                        option.badgeText === 'Beta' ? 'bg-orange-500' :
                        option.badgeText === 'New' ? 'bg-green-500' :
                        'bg-blue-500'
                      }`}>
                        {option.badgeText}
                      </span>
                    )}
                    <div className="flex items-start gap-4">
                      <div className={`flex-shrink-0 p-2 rounded-lg ${
                        option.badgeText === 'Beta' ? 'bg-orange-500/10 text-orange-400' :
                        option.badgeText === 'New' ? 'bg-green-500/10 text-green-400' :
                        option.badgeText === 'Anime Generation' ? 'bg-pink-500/10 text-pink-400' :
                        'bg-blue-500/10 text-blue-400'
                      } transition-colors duration-200`}>
                        <option.icon className="h-8 w-8" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-white mb-2">{option.title}</h3>
                        <p className="text-gray-300 text-sm">{option.description}</p>
                      </div>
                    </div>
                  </Link>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Coming Soon Section */}
        <div>
          <h3 className="text-xl font-semibold text-white mb-6">Coming Soon</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {comingSoonOptions.map((option) => (
              <div
                key={option.id}
                className="p-6 rounded-xl border border-gray-700 bg-gray-800/30"
              >
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 p-2 bg-gray-700/50 rounded-lg">
                    <option.icon className="h-8 w-8 text-gray-400" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="text-lg font-semibold text-gray-400">{option.title}</h3>
                      <span className="px-2 py-1 bg-gray-700 text-gray-300 text-xs font-medium rounded-full">
                        Soon
                      </span>
                    </div>
                    <p className="text-gray-500 text-sm">{option.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Generation;
